import { isTextEntryTarget } from '~/client/src/shared/utils/isTextEntryTarget'
import {
  isClaimOnTop,
  KeyboardClaim,
} from '~/client/src/shared/utils/keyboardClaim'

import type PhotoMarkupStore from './PhotoMarkup.store'

export const TEXT_EDITOR_ATTRIBUTE = 'data-photo-markup-text'

function applyToolShortcut(
  store: PhotoMarkupStore,
  event: KeyboardEvent,
): boolean {
  switch (event.key.toLowerCase()) {
    case 'r':
      if (event.shiftKey) return false
      store.selectTool('shape')
      store.changeShapeKind('rectangle')
      return true
    case 'o':
      if (event.shiftKey) return false
      store.selectTool('shape')
      store.changeShapeKind('circle')
      return true
    case 'l':
      store.selectTool('arrow')
      store.changeLineType(event.shiftKey ? 'arrow' : 'line')
      return true
    case 'p':
      if (event.shiftKey) {
        store.selectTool('arrow')
        store.changeLineType('polyline')
        return true
      }
      store.selectTool('shape')
      store.changeShapeKind('polygon')
      return true
    case 't':
      if (event.shiftKey) return false
      store.selectTool('text')
      return true
    case 'd':
      if (event.shiftKey) return false
      store.selectTool('draw')
      return true
    default:
      return false
  }
}

function isDeleteKey(event: KeyboardEvent): boolean {
  return event.key === 'Delete' || event.key === 'Backspace'
}

export function handlePhotoMarkupKey(
  store: PhotoMarkupStore,
  event: KeyboardEvent,
  claim: KeyboardClaim | null,
): void {
  if (!isClaimOnTop(claim) || event.defaultPrevented || store.isSaving) return
  const target = event.target as HTMLElement | null
  const isTextEntry = isTextEntryTarget(target)
  const isModified = event.metaKey || event.ctrlKey
  const isPointerDown = store.gestures.isPointerDown

  if (isModified && event.key.toLowerCase() === 'z') {
    if (isTextEntry) return
    event.preventDefault()
    if (isPointerDown) return
    if (event.shiftKey) store.redo()
    else store.undo()
    return
  }

  if (!isTextEntry && !isModified && !event.altKey && !isPointerDown) {
    if (applyToolShortcut(store, event)) {
      event.preventDefault()
      return
    }
  }

  if (isTextEntry && !target.hasAttribute(TEXT_EDITOR_ATTRIBUTE)) return

  if (store.polygonDrawing || store.polylineDrawing) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (store.polygonDrawing) store.finishPolygon()
      else store.finishPolyline()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      if (store.polygonDrawing) store.cancelPolygon()
      else store.cancelPolyline()
    }
    return
  }

  if (store.gestures.segmentAnchor && event.key === 'Escape') {
    event.preventDefault()
    store.gestures.cancelSegment()
    return
  }

  const text = store.selectedText
  if (text) {
    if (store.editingId !== text.id && isDeleteKey(event)) {
      event.preventDefault()
      store.deleteSelected()
    } else if (
      event.key === 'Escape' ||
      (event.key === 'Enter' && !event.shiftKey)
    ) {
      event.preventDefault()
      store.handleTextDone()
    }
    return
  }

  if (store.selectedPath) {
    if (isDeleteKey(event)) {
      event.preventDefault()
      store.deleteSelected()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      store.select(null)
    }
    return
  }

  if (event.key === 'Escape' && store.activeTool !== 'select') {
    event.preventDefault()
    store.selectTool('select')
  }
}
